import { ModalHeadingIdContext } from "@/components/modal/modal-context";
import { isGlobalToastTarget } from "@/components/modal/overlay-dom";
import { ContentInsetBoundary } from "@/components/layout/ContentInsetBoundary";
import { FloatingContext } from "@/components/TooltipV2";
import { IconButtonV2 } from "@/components/interactive/ButtonV2";
import { H3 } from "@/components/typography";
import { classNames } from "@/utils/classnames";
import { Transition, TransitionChild } from "@headlessui/react";
import { useRouter } from "next/router";
import React, { Fragment, ReactNode, useContext, useEffect, useId, useRef, useState } from "react";
import { OverlayLayer } from "@/components/modal/OverlayLayer";
import { OverlayProvider } from "@/components/modal/OverlayProvider";
import { useOverlayRoot, useOverlayManager } from "@/components/modal/overlay.hooks";

export { useOverlayBackgroundScrollLock as useModalBackgroundScrollLock } from "@/components/modal/overlay.hooks";

export interface SimpleModalProps {
  isOpen: boolean;
  children: ReactNode;
  onClose: () => void;
  className?: string;
  noPad?: boolean;
  modalClassName?: string;
  /** ID of this dialog's heading, distinct when dialogs are nested. */
  ariaLabelledBy?: string;

  /**
   * Default: true
   */
  automaticallyCloseOnRouteChange?: boolean;
  /**
   * Default: true
   */
  closeOnBackdropClick?: boolean;
  /**
   * Default: true
   */
  closeOnEscape?: boolean;
  /**
   * Default: false
   */
  scrollOnBackdropWheel?: boolean;
  /**
   * Default: true. Reserves scrollbar-gutter space on both edges so centered
   * content stays symmetric if the modal container scrolls. Disable for
   * edge-anchored content (e.g. SideModalCard) that must sit flush with the
   * viewport edge — the reserved gutter otherwise reads as a stray gap.
   */
  reserveScrollbarGutter?: boolean;
  /**
   * Default: false
   */
  lockBackgroundScroll?: boolean;
  /**
   * Default: true. When false, behaves as a non-modal drawer: no backdrop is
   * rendered, pointer events pass through to the page outside the panel (hover
   * and clicks keep working), and an outside pointerdown dismisses the panel
   * (without swallowing the click) when closeOnBackdropClick is enabled.
   */
  blocking?: boolean;
}

/**
 * Elements inside a container with this attribute do not dismiss a non-blocking
 * drawer on outside pointerdown. An empty value exempts the whole container;
 * a selector value exempts only matching descendants, such as table rows that
 * swap the drawer's content while clicks elsewhere dismiss it.
 */
export const DRAWER_KEEP_OPEN_ATTR = "data-drawer-keep-open";

export const SimpleModal: React.FC<SimpleModalProps> = (props) => {
  const manager = useOverlayManager();
  return manager ? (
    <SimpleModalImpl {...props} />
  ) : (
    <OverlayProvider>
      <SimpleModalImpl {...props} />
    </OverlayProvider>
  );
};

const SimpleModalImpl: React.FC<SimpleModalProps> = ({
  isOpen,
  children,
  onClose: close,
  className,
  modalClassName,
  ariaLabelledBy,
  noPad,
  automaticallyCloseOnRouteChange = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  scrollOnBackdropWheel = false,
  reserveScrollbarGutter = true,
  lockBackgroundScroll = false,
  blocking = true,
}) => {
  const generatedHeadingId = useId();
  const headingId = ariaLabelledBy ?? generatedHeadingId;
  const router = useRouter();
  const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null);
  const overlayManager = useOverlayManager();
  const overlayRoot = useOverlayRoot();

  const currentPathRef = useRef(router.asPath);
  currentPathRef.current = router.asPath;

  useEffect(() => {
    if (!automaticallyCloseOnRouteChange) {
      return;
    }
    // Only dismiss on navigation to a different path, not shallow query-only updates.
    const pathOf = (url: string) => url.split("?")[0]?.split("#")[0] ?? "";
    const closeOnPathChange = (url: string) => {
      if (pathOf(url) !== pathOf(currentPathRef.current)) {
        close();
      }
    };
    router.events.on("routeChangeStart", closeOnPathChange);
    router.events.on("hashChangeStart", close);
    return () => {
      router.events.off("routeChangeStart", closeOnPathChange);
      router.events.off("hashChangeStart", close);
    };
  }, [automaticallyCloseOnRouteChange, close, router.events]);

  const closeRef = useRef(close);
  closeRef.current = close;

  // Non-blocking drawers have no backdrop to click; dismiss on any pointerdown that
  // lands outside the overlay's content. The event is not swallowed, so the page
  // interaction beneath (row click, link, ...) still happens. Regions marked with
  // DRAWER_KEEP_OPEN_ATTR (e.g. the list the drawer was opened from, whose row
  // clicks swap the drawer's content in place) do not dismiss. Owned floating content
  // shares the layer even when it renders outside the scrollable dialog.
  useEffect(() => {
    if (blocking || !isOpen || !closeOnBackdropClick || modalRef == null) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Element)) return;
      if (isGlobalToastTarget(event.target)) return;
      if (overlayManager?.getSnapshot().at(-1)?.element.contains(modalRef) === false) return;
      const owner = modalRef.closest("[data-overlay-id]");
      if (owner?.contains(event.target)) return;
      // A nested dialog is a body-level portal, so interacting with it is not a page click.
      if (event.target.closest('[role="dialog"]') != null) return;
      const keepOpenRegion = event.target.closest(`[${DRAWER_KEEP_OPEN_ATTR}]`);
      if (keepOpenRegion != null) {
        const targetSelector = keepOpenRegion.getAttribute(DRAWER_KEEP_OPEN_ATTR);
        const keepOpenTarget = targetSelector ? event.target.closest(targetSelector) : null;
        if (
          !targetSelector ||
          (keepOpenTarget != null && keepOpenRegion.contains(keepOpenTarget))
        ) {
          return;
        }
      }
      closeRef.current();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [blocking, closeOnBackdropClick, isOpen, modalRef, overlayManager]);

  const handleBackdropWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!scrollOnBackdropWheel) return;
    event.preventDefault();
    event.stopPropagation();
    if (!modalRef) return;
    modalRef.scrollBy({
      left: event.deltaX,
      top: event.deltaY,
      behavior: "auto",
    });
  };

  if (!overlayRoot) return null;

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <OverlayLayer
        closing={!isOpen}
        lockBackgroundScroll={lockBackgroundScroll}
        blocking={blocking}
        closeOnEscape={closeOnEscape}
        blurEditableOnEscape
        backdropClassName={classNames("backdrop-blur-sm", className ?? "bg-zinc-950/70")}
        onClose={close}
      >
        <ContentInsetBoundary
          className={classNames(
            // overscroll-none (not just contain): also suppress the rubber-band bounce at the
            // scroller's own edges, e.g. in the log detail side panel.
            "fixed inset-0 z-0 overflow-y-auto overscroll-none outline-none [-webkit-overflow-scrolling:touch]",
            // Opting out has to be explicit: styles/overflow.css reserves a stable
            // gutter for ANY element whose class contains "overflow-y-auto", so
            // merely omitting the gutter class leaves the reservation in place —
            // and an edge-anchored card (SideModalCard) then stops a scrollbar's
            // width short of the viewport edge.
            reserveScrollbarGutter ? "[scrollbar-gutter:stable_both-edges]" : "no-scrollbar-gutter",
            // Pass pointer events through to the page; panel content re-enables them.
            blocking ? "pointer-events-auto" : "pointer-events-none",
            modalClassName,
          )}
          aria-labelledby={headingId}
          mode="contained"
          role="dialog"
          aria-modal={blocking}
          ref={setModalRef}
          tabIndex={-1}
        >
          <FloatingContext.Provider value={modalRef}>
            <div
              className={classNames(
                "flex mx-auto items-end sm:items-start justify-center min-h-screen text-left sm:pt-0 sm:pb-0 sm:px-8",
                noPad ? "" : "pt-4 px-4 pb-20",
              )}
            >
              {blocking && (
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-120"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-120"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div
                    onClick={() => {
                      if (
                        closeOnBackdropClick &&
                        overlayManager?.getSnapshot().at(-1)?.element.contains(modalRef)
                      ) {
                        close?.();
                      }
                    }}
                    onWheel={handleBackdropWheel}
                    data-overlay-backdrop-target=""
                    className="fixed inset-0"
                    aria-hidden="true"
                  />
                </TransitionChild>
              )}
              <ModalHeadingIdContext.Provider value={headingId}>
                {children}
              </ModalHeadingIdContext.Provider>
            </div>
          </FloatingContext.Provider>
        </ContentInsetBoundary>
      </OverlayLayer>
    </Transition>
  );
};

export function SimpleModalV2({
  lockBackgroundScroll = true,
  modalClassName,
  scrollOnBackdropWheel = true,
  ...rest
}: SimpleModalProps) {
  return (
    <SimpleModal
      lockBackgroundScroll={lockBackgroundScroll}
      modalClassName={classNames("design-system-typography", modalClassName)}
      scrollOnBackdropWheel={scrollOnBackdropWheel}
      {...rest}
    />
  );
}

export type ModalCardSize =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "8xl"
  | "full";

export function ModalCard({
  children,
  className,
  surfaceClassName,
  size = "sm",
  ...rest
}: React.ComponentProps<"div"> & {
  size?: ModalCardSize;
  surfaceClassName?: string;
}) {
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    "8xl": "max-w-[1600px]",
    full: "max-w-full",
  }[size];
  return (
    <Transition.Child
      as={Fragment}
      enter="ease-in-out duration-120"
      enterFrom="translate-y-4 sm:translate-y-0 sm:scale-75"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
      leave="ease-in duration-120"
      leaveFrom="opacity-100 translate-y-0 sm:scale-100"
      leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-75"
    >
      <ContentInsetBoundary
        mode="contained"
        className={classNames(
          "inline-block relative align-bottom",
          "rounded-md shadow-xl transition-all",
          "border-[1px] border-black/[0.03] dark:border-white/[0.03]",
          surfaceClassName ?? "bg-cds-surface-default dark:bg-cds-inset-dark",
          "px-4 pt-5 pb-4 text-left",
          "sm:mt-16 sm:align-middle sm:w-full sm:p-6",
          sizeClasses,
          className,
        )}
        {...rest}
      >
        {children}
      </ContentInsetBoundary>
    </Transition.Child>
  );
}

export function ModalHeading({ children, className, ...rest }: React.ComponentProps<"h3">) {
  const headingId = useContext(ModalHeadingIdContext);
  return (
    <H3 {...rest} id={headingId ?? rest.id} className={className}>
      {children}
    </H3>
  );
}

/**
 * Product-level modal widths. Keep this scale intentionally coarse; modal
 * content should choose a layout category rather than a Tailwind max-width.
 */
export type ModalCardV2Size = "sm" | "md" | "lg" | "xl" | "full";

const MODAL_CARD_V2_LEGACY_SIZE: Record<ModalCardV2Size, ModalCardSize> = {
  sm: "sm",
  md: "xl",
  lg: "3xl",
  xl: "5xl",
  full: "full",
};

export function ModalCardV2({
  className,
  surfaceClassName,
  size = "sm",
  ...rest
}: Omit<React.ComponentProps<typeof ModalCard>, "size"> & { size?: ModalCardV2Size }) {
  return (
    <ModalCard
      className={classNames("rounded-cds-8 !p-cds-8 shadow-xl", className)}
      surfaceClassName={surfaceClassName ?? "bg-cds-surface-default dark:bg-cds-inset-dark"}
      size={MODAL_CARD_V2_LEGACY_SIZE[size]}
      {...rest}
    />
  );
}

export function ModalHeadingV2({ children, className, ...rest }: React.ComponentProps<"h2">) {
  const headingId = useContext(ModalHeadingIdContext);
  return (
    <h2
      {...rest}
      id={headingId ?? rest.id}
      className={classNames(
        "cursor-default text-[20px] leading-6 font-medium text-cds-ink-strong dark:text-zinc-100",
        className,
      )}
    >
      {children}
    </h2>
  );
}

type ModalCloseButtonV2Props = Omit<
  React.ComponentProps<typeof IconButtonV2>,
  "icon" | "size" | "variant"
>;

export function ModalCloseButtonV2({ className, ...rest }: ModalCloseButtonV2Props) {
  return (
    <IconButtonV2
      className={classNames("absolute right-cds-12 top-cds-12", className)}
      icon="close"
      size="sm"
      variant="tertiary"
      {...rest}
    />
  );
}
